// Erasure holes live in pattern coordinates and follow its transform and mirror.
export const MAX_PATTERN_CUTS=600;
export const PATTERN_BOUNDS={x:-100,y:-150,width:300,height:450};
const finite=value=>typeof value==='number'&&Number.isFinite(value);
const round=value=>Math.round(value*1000)/1000;
const intersects=({x,y,r})=>Math.hypot(x-Math.max(-100,Math.min(200,x)),y-Math.max(-150,Math.min(300,y)))<=r;

export function normalizePatternCuts(input){
  if(!Array.isArray(input))return[];
  const seen=new Set(),cuts=[];
  for(const value of input.slice(0,MAX_PATTERN_CUTS)){
    if(!value||!finite(value.x)||!finite(value.y)||!finite(value.r)||value.x< -300||value.x>400||value.y< -350||value.y>500||value.r<.1||value.r>200)continue;
    const cut={x:round(value.x),y:round(value.y),r:round(value.r)};
    const key=`${cut.x}:${cut.y}:${cut.r}`;
    if(!seen.has(key)&&intersects(cut)){seen.add(key);cuts.push(cut);}
  }
  return cuts;
}

export function patternPointCovered(layer,localPoint){
  if(!localPoint||!finite(localPoint.x)||!finite(localPoint.y))return false;
  return (layer.patternCuts||[]).some(c=>Math.hypot(localPoint.x-c.x,localPoint.y-c.y)<=c.r);
}

export function isLocallyErasableLayer(layer){
  return !!layer&&(['sticker','stroke'].includes(layer.kind)||(layer.kind==='pattern'&&layer.type!=='dots'));
}

export function layerErasePoint(layer,point){
  const scale=finite(layer.scale)&&layer.scale>0?layer.scale:1;
  const radians=-(layer.rotation||0)*Math.PI/180,dx=point.x-layer.x,dy=point.y-layer.y;
  const centered=layer.kind==='sticker';
  let x=(centered?0:50)+(dx*Math.cos(radians)-dy*Math.sin(radians))/scale;
  const y=(centered?0:75)+(dx*Math.sin(radians)+dy*Math.cos(radians))/scale;
  if(layer.patternFlipX)x=100-x;
  return{x,y};
}

function cutArtwork(value,point,radius){
  const cut=normalizePatternCuts([{...point,r:Math.max(.1,Math.min(200,radius))}])[0];
  if(!cut)return value;
  const old=normalizePatternCuts(value.patternCuts);
  if(old.some(c=>Math.hypot(c.x-cut.x,c.y-cut.y)+cut.r<=c.r+.001))return value;
  const retained=old.filter(c=>Math.hypot(c.x-cut.x,c.y-cut.y)+c.r>cut.r+.001);
  if(retained.length>=MAX_PATTERN_CUTS)return value;
  return{...value,patternCuts:[...retained,cut]};
}

export function eraseLayerAt(layer,point,radius){
  if(!isLocallyErasableLayer(layer)||layer.visible===false||layer.opacity===0||!point||!finite(point.x)||!finite(point.y)||!finite(radius)||radius<=0)return layer;
  const scale=finite(layer.scale)&&layer.scale>0?layer.scale:1;
  return cutArtwork(layer,layerErasePoint(layer,point),radius/scale);
}

export function eraseFrenchAt(french,point,radius){
  if(!french||!point||!finite(point.x)||!finite(point.y)||!finite(radius)||radius<=0)return french;
  return cutArtwork(french,point,radius);
}

// Keep the ordinary-pattern API for existing callers and saved designs.
export function erasePatternAt(layer,point,radius){
  return layer?.kind==='pattern'?eraseLayerAt(layer,point,radius):layer;
}
